import * as React from "react";

import { ActionIcon, Flex, Group, Tooltip } from "@mantine/core";
import { IconGripVertical, IconTrash } from "@tabler/icons-react";
import { DraggableProvided } from "react-beautiful-dnd";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppState } from "../../hooks/useAppState";
import { useCountEvent } from "../../hooks/useCountEvent";
import { ChecklistItem } from "../../lib/model/checklistAppState";
import { autofocusFieldSlice, librarySlice } from "../../lib/state/reducer";

import { ChecklistElementAdjustmentsMenu } from "./ChecklistElementAdjustmentsMenu";
import { VanillaEditableText } from "./text/VanillaEditableText";

export const ChecklistItemEditor = ({
  checklistItem,
  dragHandleProps,
  onRemove,
  autoAdd,
}: {
  checklistItem: ChecklistItem;
  dragHandleProps: DraggableProvided["dragHandleProps"];
  onRemove: () => void;
  autoAdd?: () => void;
}): JSX.Element => {
  const reduxDispatch = useAppDispatch();
  // TODO: make this a preference
  // TODO: restore this? maybe not?
  // const clickOutsideRef = useClickOutside(() => {
  //   if (checklistItem.item.content === "" && checklistItem.setting.content === "" && autoAdd) {
  //     onRemove();
  //   }
  // });

  const itemInputRef = React.useRef<HTMLInputElement>(null);
  const settingInputRef = React.useRef<HTMLInputElement>(null);
  const isItemAutofocused = useAppState((state) => state.autofocusField === checklistItem.item.id);

  const countEvent = useCountEvent();

  const onItemEnter = React.useCallback(() => {
    if (settingInputRef.current && isItemAutofocused) {
      settingInputRef.current.focus();
    } else {
      if (itemInputRef.current) {
        itemInputRef.current.blur();
      }
    }
  }, [reduxDispatch, settingInputRef.current, itemInputRef.current]);

  const onItemSave = React.useCallback(
    (newValue: string) => {
      reduxDispatch(
        librarySlice.actions.updateChecklistElement({
          elementId: checklistItem.id,
          elementField: "item",
          newValue,
        })
      );
      countEvent("checklist-item-item-edited");
    },
    [reduxDispatch, checklistItem.id, countEvent]
  );
  const onSettingSave = React.useCallback(
    (newValue: string) => {
      reduxDispatch(
        librarySlice.actions.updateChecklistElement({
          elementId: checklistItem.id,
          elementField: "setting",
          newValue,
        })
      );
      countEvent("checklist-item-setting-edited");
      // if (autoAdd) {
      //   autoAdd();
      // }
    },
    [reduxDispatch, checklistItem.id, countEvent]
  );

  const onItemEmptyEnter = React.useCallback(() => {
    if (autoAdd && checklistItem.setting.content === "") {
      reduxDispatch(autofocusFieldSlice.actions.setAutofocusField(checklistItem.setting.id));
    }
  }, [autoAdd, reduxDispatch, checklistItem.setting.content]);

  const onSettingEmptyEnter = React.useCallback(() => {
    if (autoAdd) {
      autoAdd();
    }
  }, [autoAdd]);

  const onSettingEmptyTab = React.useCallback(() => {
    if (autoAdd) {
      autoAdd();
    }
  }, [autoAdd]);

  // TODO most of these icons need a pass at their z-index relative to the addition
  // bar (css?)
  return (
    <Flex
      wrap={"nowrap"}
      align="center"
      style={{ backgroundColor: checklistItem.styles?.backgroundColor }}
    >
      <ActionIcon variant="transparent" size="sm" {...dragHandleProps} tabIndex={-1}>
        <IconGripVertical size={16} />
      </ActionIcon>
      <Flex wrap={"nowrap"} style={{ flexGrow: 1, flexShrink: 1 }} align="center">
        <VanillaEditableText
          ref={itemInputRef}
          text={checklistItem.item}
          placeholder="Item"
          extraPadding={10}
          onSave={onItemSave}
          onEnter={onItemEnter}
          onEmptyEnter={onItemEmptyEnter}
          color={checklistItem.styles?.color}
          styles={{ ...checklistItem.styles }}
        />
        <VanillaEditableText
          ref={settingInputRef}
          text={checklistItem.setting}
          placeholder="Setting"
          extraPadding={10}
          justify="right"
          onSave={onSettingSave}
          onEmptyEnter={onSettingEmptyEnter}
          onEmptyTab={onSettingEmptyTab}
          color={checklistItem.styles?.color}
          styles={{ ...checklistItem.styles }}
        />
      </Flex>
      <Group spacing={0} ml={5} align="center" style={{ flex: "0 0 auto" }}>
        <ChecklistElementAdjustmentsMenu checklistElement={checklistItem} />
        <Tooltip withArrow label="Delete" openDelay={500}>
          <ActionIcon size="sm" onClick={() => onRemove()} tabIndex={-1}>
            <IconTrash size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Flex>
  );
};
